import enTranslations from '../translations/en';

/**
 * Static translations hook.
 *
 * All page text is hardcoded in the bundle (src/translations/en.js, generated
 * once from the DB). Nothing is fetched at runtime — no network requests, and
 * the site is English-only. Visa/blog content that is admin-managed is
 * fetched through the dedicated visaService / resources APIs, not here.
 *
 * `t(key, fallback)` does a dot-path lookup in the static bundle and falls
 * back to the provided English fallback (or the key itself) when missing.
 */
export function useTranslations(category = 'home', languageCode = 'en') {
  const rawTranslations = enTranslations[category] || {};

  // Universal category extraction logic (same as the old DB-backed hook):
  // 1. Count total keys at top level
  // 2. If the category key exists:
  //    a) If it's the ONLY top-level key -> extract it (visapathways, feesscope)
  //    b) If category has nested objects -> extract it (howitworks)
  //    c) If category + many siblings that look like sections -> DON'T extract (home)
  let translations = rawTranslations;
  const topLevelKeys = Object.keys(rawTranslations);

  if (category && rawTranslations[category] && typeof rawTranslations[category] === 'object') {
    const categoryData = rawTranslations[category];

    // Case 1: Category is the only top-level key
    if (topLevelKeys.length === 1 && topLevelKeys[0] === category) {
      translations = categoryData;
    }
    // Case 2: Multiple top-level keys - check if category has nested structure
    else {
      const categoryKeyCount = Object.keys(categoryData).length;
      const hasNestedObjects = Object.values(categoryData).some(
        val => typeof val === 'object' && val !== null && !Array.isArray(val)
      );
      if ((categoryKeyCount >= 5 || hasNestedObjects) && category !== 'home') {
        translations = categoryData;
      }
    }
  }

  const t = (key, fallback = key) => {
    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    // Branch that also has a direct value (e.g. services.business = "Apply For a Visa" and services.business.detail = "...") is stored as { _: "Apply For a Visa", detail: "..." }; return the direct value for the parent key.
    if (value !== undefined && typeof value === 'object' && value !== null && '_' in value) {
      value = value._;
    }
    // Treat empty strings as missing so the English fallback shows instead of blank text
    return (value !== undefined && value !== '') ? value : fallback;
  };

  return { t, translations, loading: false, error: null };
}

export default useTranslations;
